import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  CreateCalendarEvent,
  CreateCalendarReminder,
  UpdateCalendarEvent,
  UpdateCalendarReminder,
} from '@/models/CalendarEntry';
import {
  createCalendarEvent,
  createCalendarReminder,
  deleteCalendarEvent,
  deleteCalendarReminder,
  getPropertyCalendarEntries,
  updateCalendarEvent,
  updateCalendarReminder,
} from '@/services/api/calendar';

export const useCalendarEntries = (propertyId?: string) => {
  const queryClient = useQueryClient();

  const { data: allCalendarEntries, isFetching: isFetchingAllCalendarEntries } = useQuery({
    queryKey: ['calendar', propertyId],
    queryFn: () => getPropertyCalendarEntries(propertyId),
    enabled: Boolean(propertyId),
  });

  const {
    mutate: createCalendarEventRequest,
    isPending: isCreatingCalendarEvent,
    isSuccess: isCalendarEventCreated,
    error: createCalendarEventError,
  } = useMutation({
    mutationFn: createCalendarEvent,
    onSuccess: ({ propertyId }) => {
      queryClient.invalidateQueries({ queryKey: ['calendar', propertyId] });
    },
  });

  const {
    mutate: updateCalendarEventRequest,
    isPending: isUpdatingCalendarEvent,
    isSuccess: isCalendarEventUpdated,
    error: updateCalendarEventError,
  } = useMutation({
    mutationFn: updateCalendarEvent,
    onSuccess: ({ propertyId }) => {
      queryClient.invalidateQueries({ queryKey: ['calendar', propertyId] });
    },
  });

  const {
    mutate: deleteCalendarEventRequest,
    isPending: isDeletingCalendarEvent,
    isSuccess: isCalendarEventDeleted,
    error: deleteCalendarEventError,
  } = useMutation({
    mutationFn: ({ id, propertyId: _propertyId }: { id: string; propertyId?: string }) =>
      deleteCalendarEvent({ id }),
    onSuccess: (_, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: ['calendar', propertyId] });
    },
  });

  const {
    mutate: createCalendarReminderRequest,
    isPending: isCreatingCalendarReminder,
    isSuccess: isCalendarReminderCreated,
    error: createCalendarReminderError,
  } = useMutation({
    mutationFn: createCalendarReminder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar', propertyId] });
    },
  });

  const {
    mutate: updateCalendarReminderRequest,
    isPending: isUpdatingCalendarReminder,
    isSuccess: isCalendarReminderUpdated,
    error: updateCalendarReminderError,
  } = useMutation({
    mutationFn: updateCalendarReminder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar', propertyId] });
    },
  });

  const {
    mutate: deleteCalendarReminderRequest,
    isPending: isDeletingCalendarReminder,
    isSuccess: isCalendarReminderDeleted,
    error: deleteCalendarReminderError,
  } = useMutation({
    mutationFn: ({ id, propertyId: _propertyId }: { id: string; propertyId?: string }) =>
      deleteCalendarReminder({ id }),
    onSuccess: (_, { propertyId }) => {
      queryClient.invalidateQueries({ queryKey: ['calendar', propertyId] });
    },
  });

  return {
    allCalendarEntries,
    isFetchingAllCalendarEntries,

    createCalendarEvent: (data: CreateCalendarEvent) =>
      propertyId && createCalendarEventRequest({ propertyId, data }),
    isCreatingCalendarEvent,
    isCalendarEventCreated,
    createCalendarEventError,

    updateCalendarEvent: (id: string, data: UpdateCalendarEvent) =>
      updateCalendarEventRequest({ id, data }),
    isUpdatingCalendarEvent,
    isCalendarEventUpdated,
    updateCalendarEventError,

    deleteCalendarEvent: (id: string) => deleteCalendarEventRequest({ id, propertyId }),
    isDeletingCalendarEvent,
    isCalendarEventDeleted,
    deleteCalendarEventError,

    createCalendarReminder: (data: CreateCalendarReminder) =>
      propertyId && createCalendarReminderRequest({ propertyId, data }),
    isCreatingCalendarReminder,
    isCalendarReminderCreated,
    createCalendarReminderError,

    updateCalendarReminder: (id: string, data: UpdateCalendarReminder) =>
      updateCalendarReminderRequest({ id, data }),
    isUpdatingCalendarReminder,
    isCalendarReminderUpdated,
    updateCalendarReminderError,

    deleteCalendarReminder: (id: string) => deleteCalendarReminderRequest({ id, propertyId }),
    isDeletingCalendarReminder,
    isCalendarReminderDeleted,
    deleteCalendarReminderError,
  };
};
